import React, { FC, useEffect, useRef, useState } from 'react';
import {
    Dialog,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import styles from './ImageViewerComponent.module.scss';
import boddVariables from '../../../../variables.module.scss';

interface ImageViewerComponentProps {
    imageUrls: string[];
    open: boolean;
    onClose: () => void;
    initialIndex: number;
}

const MinZoom = 1;
const MaxZoom = 8;

export const getFilename = (url: string) => url.split('/').pop()?.split('?').shift() ?? '';

/**
 * Full-screen image carousel with chevron navigation and cursor-anchored zoom/pan.
 */
const ImageViewerComponent: FC<ImageViewerComponentProps> = (props: ImageViewerComponentProps) => {
    const {
        imageUrls, open, onClose, initialIndex,
    } = props;

    const [currentImageIndex, setCurrentImageIndex] = useState<number>(initialIndex);
    const [zoom, setZoom] = useState<number>(MinZoom);
    const [pan, setPan] = useState<{ x: number, y: number }>({ x: 0, y: 0 });
    const viewportRef = useRef<HTMLDivElement>(null);
    const dragState = useRef<{ startX: number, startY: number, panX: number, panY: number } | null>(null);

    const resetZoom = () => {
        setZoom(MinZoom);
        setPan({ x: 0, y: 0 });
    };

    useEffect(() => {
        if (open) {
            setCurrentImageIndex(initialIndex);
            resetZoom();
        }
    }, [open, initialIndex]);

    // a shorter list (refetch or session change) would otherwise leave the index past the end
    useEffect(() => {
        if (open && currentImageIndex > imageUrls.length - 1) {
            setCurrentImageIndex(0);
            onClose();
        }
    }, [open, imageUrls.length, currentImageIndex, onClose]);

    const changeImage = (direction: number) => {
        setCurrentImageIndex((prev) => prev + direction);
        resetZoom();
    };

    const zoomAt = (clientX: number, clientY: number, nextZoom: number) => {
        const clamped = Math.min(MaxZoom, Math.max(MinZoom, nextZoom));
        if (clamped === MinZoom) {
            resetZoom();
            return;
        }
        const viewport = viewportRef.current?.getBoundingClientRect();
        if (!viewport) {
            setZoom(clamped);
            return;
        }
        const ratio = clamped / zoom;
        const cursorX = clientX - viewport.left - viewport.width / 2;
        const cursorY = clientY - viewport.top - viewport.height / 2;
        setPan((prev) => ({
            x: cursorX - (cursorX - prev.x) * ratio,
            y: cursorY - (cursorY - prev.y) * ratio,
        }));
        setZoom(clamped);
    };

    const zoomAtCentre = (nextZoom: number) => {
        const viewport = viewportRef.current?.getBoundingClientRect();
        zoomAt(
            (viewport?.left ?? 0) + (viewport?.width ?? 0) / 2,
            (viewport?.top ?? 0) + (viewport?.height ?? 0) / 2,
            nextZoom,
        );
    };

    const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
        zoomAt(e.clientX, e.clientY, zoom * (e.deltaY < 0 ? 1.25 : 0.8));
    };

    const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        zoomAt(e.clientX, e.clientY, zoom > MinZoom ? MinZoom : 2.5);
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (zoom === MinZoom) {
            return;
        }
        e.preventDefault();
        dragState.current = {
            startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y,
        };
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!dragState.current) {
            return;
        }
        const drag = dragState.current;
        setPan({
            x: drag.panX + (e.clientX - drag.startX),
            y: drag.panY + (e.clientY - drag.startY),
        });
    };

    const stopDragging = () => {
        dragState.current = null;
    };

    const currentUrl = imageUrls[currentImageIndex] ?? '';

    return (
        <Dialog
            fullWidth
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { maxWidth: 'unset' } }}
            data-testid="ImageViewerComponent"
        >
            <Stack
                direction="row"
                justifyContent="space-evenly"
                alignItems="flex-start"
                sx={{ bgcolor: boddVariables.boddBlack, padding: '0' }}
            >
                {currentImageIndex > 0 && (
                    <IconButton
                        aria-label="Previous image"
                        sx={{ position: 'absolute', left: '0', top: '48%', zIndex: 1 }}
                        onClick={() => changeImage(-1)}
                    >
                        <ChevronLeftIcon sx={{ color: boddVariables.white, fontSize: 32 }} />
                    </IconButton>
                )}
                {currentImageIndex < imageUrls.length - 1 && (
                    <IconButton
                        aria-label="Next image"
                        sx={{ position: 'absolute', left: '92%', top: '48%', zIndex: 1 }}
                        onClick={() => changeImage(1)}
                    >
                        <ChevronRightIcon sx={{ color: boddVariables.white, fontSize: 32 }} />
                    </IconButton>
                )}
                <Stack direction="row" sx={{ position: 'absolute', right: '8px', top: '8px', zIndex: 1 }}>
                    <IconButton aria-label="Zoom in" onClick={() => zoomAtCentre(zoom * 1.5)} disabled={zoom >= MaxZoom}>
                        <ZoomInIcon sx={{ color: boddVariables.white, fontSize: 28 }} />
                    </IconButton>
                    <IconButton aria-label="Zoom out" onClick={() => zoomAtCentre(zoom / 1.5)} disabled={zoom <= MinZoom}>
                        <ZoomOutIcon sx={{ color: boddVariables.white, fontSize: 28 }} />
                    </IconButton>
                </Stack>

                <div
                    ref={viewportRef}
                    role="presentation"
                    onWheel={handleWheel}
                    onDoubleClick={handleDoubleClick}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={stopDragging}
                    onMouseLeave={stopDragging}
                    style={{
                        display: 'flex',
                        // the image is centred so the cursor-anchored zoom maths matches its transform origin
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '900px',
                        overflow: 'hidden',
                        cursor: zoom > MinZoom ? 'grab' : 'zoom-in',
                    }}
                >
                    <img
                        alt={getFilename(currentUrl)}
                        src={currentUrl}
                        className={styles.LargeImage}
                        draggable={false}
                        style={{
                            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                            transformOrigin: 'center center',
                        }}
                    />
                </div>
            </Stack>
            <Typography sx={{
                position: 'absolute',
                left: '0',
                top: 'calc(100% - 32px)',
            }}
            >
                {getFilename(currentUrl)}
            </Typography>
        </Dialog>
    );
};

export default ImageViewerComponent;
